import { useEffect, useRef, useState } from 'react';
import { ApiError, Button, SlideToConfirm, api, brl, chime, cx, haptic, km, minutes } from '@mobi/web';
import type { RideOffer } from '../useDriver.js';

/**
 * Ritmo de aviso, do início ao fim da janela de resposta.
 *
 * Toque de telefone tocando, não notificação discreta: pulso junto do
 * outro o tempo todo, e mais junto ainda perto do fim — do mesmo jeito
 * que um alarme de verdade escala em vez de bipar uma vez só e esperar.
 */
const PULSE_MS = 900;
const URGENT_BELOW_MS = 5_000;
const URGENT_PULSE_MS = 420;

/**
 * Oferta de corrida em tela cheia, com contagem regressiva.
 *
 * Tela cheia de propósito: o motorista tem 15 segundos e está dirigindo.
 * Um card discreto no canto seria perdido.
 */
export function OfferModal({
  offer,
  onResolved,
}: {
  offer: RideOffer;
  onResolved: () => void;
}) {
  const [left, setLeft] = useState(offer.expiresInMs);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const alerted = useRef(false);
  const lastPulseAt = useRef(0);

  useEffect(() => {
    /**
     * Um pulso é um zumbido de verdade (não um toque único fraco demais
     * para sentir com o celular no suporte do carro) mais um par de
     * bipes — "bip-bip", não um tom solto. `strong` empilha um terceiro
     * tom para a reta final. Os três tons vão numa chamada só a `chime`,
     * agendados no relógio do Web Audio — não em `setTimeout`, que sob
     * esta cadência (a cada 900ms, depois a cada 420ms) se acumulava e o
     * som parava de tocar depois de alguns pulsos.
     */
    function ring(strong: boolean) {
      haptic(strong ? [0, 160, 70, 160, 70, 160] : [0, 140, 70, 140]);
      chime(
        strong
          ? [
              { freq: 1046, at: 0, durationMs: 110, gain: 0.16 },
              { freq: 1318, at: 120, durationMs: 110, gain: 0.16 },
              { freq: 1568, at: 240, durationMs: 130, gain: 0.16 },
            ]
          : [
              { freq: 880, at: 0, durationMs: 110, gain: 0.16 },
              { freq: 1160, at: 120, durationMs: 110, gain: 0.16 },
            ],
      );
    }

    if (!alerted.current) {
      alerted.current = true;
      ring(false);
    }
    const started = Date.now();
    // O pulso de entrada já avisou; o próximo só depois de uma cadência
    // inteira, senão o segundo aviso soaria colado no primeiro.
    lastPulseAt.current = started;

    const timer = setInterval(() => {
      const remaining = offer.expiresInMs - (Date.now() - started);
      setLeft(remaining);

      // Pulso repetido enquanto a oferta segue em aberto — junto do
      // início ao fim, e mais junto ainda perto do fim.
      const urgentNow = remaining <= URGENT_BELOW_MS;
      const cadence = urgentNow ? URGENT_PULSE_MS : PULSE_MS;
      if (remaining > 0 && Date.now() - lastPulseAt.current >= cadence) {
        lastPulseAt.current = Date.now();
        ring(urgentNow);
      }

      // Não decide nada no cliente: quando o tempo acaba, quem marca a
      // oferta como perdida é o servidor. A tela só sai da frente.
      if (remaining <= 0) {
        clearInterval(timer);
        onResolved();
      }
    }, 100);
    return () => clearInterval(timer);
  }, [offer, onResolved]);

  async function respond(action: 'accept' | 'decline') {
    setBusy(action);
    setError(null);
    try {
      await api(`/api/rides/${offer.rideId}/${action}`, { method: 'POST', body: {} });
      haptic();
      onResolved();
    } catch (err) {
      // Outro motorista foi mais rápido, ou a oferta expirou: sai da tela
      // em vez de deixar o motorista brigando com um botão morto.
      if (err instanceof ApiError && err.status === 409) onResolved();
      else setError(err instanceof ApiError ? err.message : 'não foi possível responder');
    } finally {
      setBusy(null);
    }
  }

  const seconds = Math.max(0, Math.ceil(left / 1000));
  const pct = Math.max(0, Math.min(100, (left / offer.expiresInMs) * 100));
  const urgent = left > 0 && left <= URGENT_BELOW_MS;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface">
      {/* Brilho pulsante na borda, só na reta final — mexer a tela
          inteira (testado e descartado) lia como bug, não como aviso;
          só a borda pulsando não briga com o resto se redesenhando a
          cada 100ms do cronômetro. */}
      {urgent && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-10 animate-[ring-pulse_.42s_ease-in-out_infinite] shadow-[inset_0_0_0_6px_var(--c-danger)]"
        />
      )}

      <div className="h-1.5 w-full bg-surface-2">
        <div
          className={cx(
            'h-full transition-[width] duration-100 ease-linear',
            urgent ? 'bg-danger' : 'bg-brand',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-center px-6 py-8">
        <div className="flex items-baseline justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand">Nova corrida</p>
          <p className={cx('tabular text-2xl font-bold', urgent && 'text-danger')}>{seconds}s</p>
        </div>

        <p className="tabular mt-6 text-5xl font-bold">{brl(offer.fareCents)}</p>
        <p className="mt-1 text-muted">
          {offer.vehicleType === 'moto' ? 'Moto' : 'Carro'} · {km(offer.rideDistanceMeters)} ·{' '}
          {minutes(offer.rideDurationSeconds)}
        </p>

        <div className="mt-8 space-y-5">
          <div className="flex gap-3">
            <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-ink" />
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-faint">Embarque</p>
              <p className="font-medium">{offer.originAddress}</p>
              <p className="tabular text-sm text-muted">
                {km(offer.distanceToPickupMeters)} · {minutes(offer.pickupEtaSeconds)} até lá
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-brand" />
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-faint">Destino</p>
              <p className="font-medium">{offer.destinationAddress}</p>
            </div>
          </div>
        </div>

        {error && <p className="mt-6 text-sm text-danger">{error}</p>}
      </div>

      <div className="space-y-3 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SlideToConfirm
          label="Deslize para aceitar"
          loading={busy === 'accept'}
          disabled={busy !== null}
          onConfirm={() => void respond('accept')}
        />
        <Button
          variant="ghost"
          full
          loading={busy === 'decline'}
          disabled={busy !== null}
          onClick={() => void respond('decline')}
        >
          Recusar
        </Button>
      </div>
    </div>
  );
}
